import { STORAGE_KEYS } from '@/constants/CheckoutConstants'
import { translate } from '@/plugins/i18n'
import ApiController from '@/services/ApiController'
import { clearBraintreeClient } from '@/services/Braintree'
import { WALKER_COMMANDS } from '@/services/helpers/ElbwalkerHelper'
import MessageHelper from '@/services/helpers/MessageHelper'
import { useCartStore } from '@/stores/cart'
import {
  CheckoutStrategy,
  GuestCheckoutStrategy,
  OpcCheckoutStrategy,
  RegularCheckoutStrategy
} from '@/stores/checkoutStrategies'
import { useCustomerStore } from '@/stores/customer'
import { useGuestCheckoutStore } from '@/stores/guestCheckout'
import { usePaymentStore } from '@/stores/payment'
import { useRegularCheckoutStore } from '@/stores/regularCheckout'
import { buildBasePayload, handleCheckoutError } from '@/utils/checkoutHelpers'
import { elb } from '@elbwalker/walker.js'
import { RemovableRef, useSessionStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { toRaw } from 'vue'

export interface CheckoutAddressError {
  description: string
  field?: string
  fields?: string[]
  address_type?: string
}

interface CheckoutState {
  // State
  isOrderPending: boolean
  forms: Record<string, { validate: () => Promise<any>; $el: Element }>
  storeDataToCustomer: boolean
  checkoutError?: string | null
  checkoutAddressError: CheckoutAddressError | null
  // Customer Service Fields
  csReasonForOrder: string | null
  csCreateNoCredits: boolean
  csCreateNoConfirmationMail: boolean
  csCreateNoShippingMail: boolean
  csCreateNoInvoiceMail: boolean
  csComment?: string | null
  csScheduledShipmentDate?: RemovableRef<string | null>
}

export enum CheckoutErrorType {
  FALLBACK_CREATE_ORDER_ERROR = 'create_order_error',
  PAYMENT_METHOD_ERROR = 'payment_error',
  BILLING_ADDRESS_ERROR = 'billing_address_error',
  SHIPPING_ADDRESS_ERROR = 'shipping_address_error',
  CART_OWNERSHIP_ERROR = 'cart_ownership_error'
}

export const SESSION_EMAIL_KEY = 'checkout_email'

export const useNewCheckoutStore = defineStore('newCheckout', {
  state: (): CheckoutState => ({
    // State
    isOrderPending: false,
    forms: {},
    storeDataToCustomer: true,
    checkoutError: null,
    checkoutAddressError: null,
    // Customer Service Fields
    csReasonForOrder: null,
    csCreateNoCredits: false,
    csCreateNoConfirmationMail: false,
    csCreateNoShippingMail: false,
    csCreateNoInvoiceMail: false,
    csComment: null,
    csScheduledShipmentDate: useSessionStorage(
      STORAGE_KEYS.CS_SCHEDULED_SHIPMENT_DATE,
      null
    )
  }),
  actions: {
    registerForm(
      formName: string,
      form: { validate: () => Promise<any>; $el: Element }
    ) {
      this.forms[formName] = form
    },
    unregisterForm(formName: string) {
      delete this.forms[formName]
    },
    async validateForms(): Promise<boolean> {
      // Check if admin is logged in as customer via cookie
      let isAdminLoggedInAsCustomer = false
      if (typeof document !== 'undefined') {
        const { default: CookieHelper } = await import(
          '@/services/helpers/CookieHelper'
        )
        const cookieValue = CookieHelper.getCookie('original-customer-id')
        isAdminLoggedInAsCustomer = !!cookieValue
      }

      // Filter out ageCheck form if admin is logged in as another customer
      const formsToValidate = Object.entries(this.forms)
        .filter(([formName]) => {
          if (formName === 'ageCheck' && isAdminLoggedInAsCustomer) {
            return false
          }
          return true
        })
        .map(([, form]) => form)

      const results = await Promise.all(
        formsToValidate.map((form) => form.validate())
      )

      const firstInvalidIndex = results.findIndex((result) => !result)
      if (firstInvalidIndex !== -1) {
        const { $el: element } = formsToValidate[firstInvalidIndex]
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return false
      }

      return true
    },
    /**
     * Places an order for authenticated users with an existing cart.
     * Used for the regular multi-step checkout flow
     *
     * Flow: validateForms → beforeOrder → buildPayload → 3DS → placeOrder API
     *
     * @param validateForms - Whether to validate forms before placing the order (default: true)
     */
    async placeOrder(validateForms: boolean = true): Promise<any> {
      const strategy = new RegularCheckoutStrategy()
      const customerStore = useCustomerStore()

      return await this._placeOrderInternal(strategy, {
        validateForms,
        email: strategy.getEmail() ?? customerStore.profile.email
      })
    },

    /**
     * Places an order for authenticated users, adding a product to cart first.
     * Convenience method that combines cart.addItem + placeOrder.
     *
     * @param productId - Product ID to add to cart
     * @param campaignId - Campaign ID for the product
     * @param qty - Quantity to add (default: 1)
     * @param validateForms - Whether to validate forms (default: true)
     */
    async placeOrderWithProduct(
      productId: number,
      campaignId: number,
      qty: number = 1,
      validateForms: boolean = true
    ): Promise<any> {
      this.isOrderPending = true
      const cartStore = useCartStore()
      await cartStore.addItem({ productId, campaignId, qty }, false)
      return await this.placeOrder(validateForms)
    },

    /**
     * Places an order for guest (non-authenticated) users.
     * Uses the express order API endpoint which doesn't require customer authentication.
     *
     * Flow: validateForms → buildPayload → 3DS → placeExpressOrder API
     *
     * @param validateForms - Whether to validate forms before placing the order (default: true)
     */
    async placeExpressOrder(validateForms: boolean = true): Promise<any> {
      const strategy = new GuestCheckoutStrategy()

      return await this._placeOrderInternal(strategy, {
        validateForms,
        email: strategy.getEmail()!
      })
    },

    /**
     * Places an order from the One-Page Checkout (OPC) flow.
     * Adds product to cart and places order in a single flow.
     * Uses express order API for PayPal Express, Apple Pay, and Google Pay.
     *
     * Flow: validateForms → addProductToCart → buildPayload → 3DS → placeOrder/placeExpressOrder API
     *
     * @param productId - Product ID to purchase
     * @param campaignId - Campaign ID for the product
     * @param qty - Quantity to purchase (default: 1)
     * @param validateForms - Whether to validate forms (default: true)
     */
    async placeOpcOrder(
      productId: number,
      campaignId: number,
      qty: number = 1,
      validateForms: boolean = true
    ): Promise<any> {
      const strategy = new OpcCheckoutStrategy()
      const customerStore = useCustomerStore()

      // Configure the strategy with product details
      strategy.setProduct(productId, campaignId, qty)

      return await this._placeOrderInternal(strategy, {
        validateForms,
        email: strategy.getEmail() ?? customerStore.profile?.email
      })
    },

    /**
     * Internal unified method for placing orders using the strategy pattern.
     *
     * This method orchestrates the checkout flow:
     * 1. Validate forms and checkout data
     * 2. Execute strategy's beforeOrder hook (e.g., add to cart, set defaults)
     * 3. Build payload (base + addresses + payment + CS fields)
     * 4. Execute strategy's modifyPayload hook
     * 5. Perform 3DS check
     * 6. Update customer data (for authenticated non-express orders)
     * 7. Call appropriate API endpoint (placeOrder or placeExpressOrder)
     * 8. Run cleanup
     *
     * @param strategy - The checkout strategy to use
     * @param options - Options for the order placement
     */
    async _placeOrderInternal(
      strategy: CheckoutStrategy,
      options: {
        validateForms?: boolean
        email: string | null | undefined
      }
    ): Promise<any> {
      const { validateForms = true, email } = options

      // Determine checkout characteristics from strategy
      const isGuest = strategy.isGuest
      const useExpressOrder = strategy.shouldUseExpressOrder()

      // Step 1: Validate forms and checkout data
      if (validateForms) {
        const formsValid = await this.validateForms()
        if (!formsValid) {
          this.isOrderPending = false
          MessageHelper.addErrorMessage(translate('checkout.error.formInvalid'))
          return
        }

        if (!strategy.validateCheckoutData()) {
          this.isOrderPending = false
          MessageHelper.addErrorMessage(translate('checkout.error.formInvalid'))
          return
        }
      }

      this.isOrderPending = true

      // Clear any previous error state so the composable reads fresh values
      this.checkoutError = null
      this.checkoutAddressError = null

      // Step 2: Execute strategy's beforeOrder hook
      if (strategy.beforeOrder) {
        await strategy.beforeOrder()
      }

      const cartStore = useCartStore()
      const customerStore = useCustomerStore()

      // Step 3: Build payload
      let payload = buildBasePayload(
        cartStore,
        email ?? customerStore.profile?.email,
        strategy.getSubscribeToNewsletter(),
        strategy.getCheckoutType()
      )

      // Add address fields
      try {
        payload = await strategy.addAddressFieldsToPayload(
          payload,
          this.storeDataToCustomer
        )
      } catch (error: any) {
        const originalDescription = error.description
        const errorType =
          error.address_type === 'shipping_address'
            ? CheckoutErrorType.SHIPPING_ADDRESS_ERROR
            : CheckoutErrorType.BILLING_ADDRESS_ERROR
        error.description = errorType
        this.checkoutError = errorType
        this.checkoutAddressError = {
          description: originalDescription,
          field: error.field,
          fields: error.fields,
          address_type: error.address_type
        }
        this.isOrderPending = false
        throw error
      }

      // Add payment fields
      try {
        payload = await strategy.addPaymentFieldsToPayload(
          payload,
          this.storeDataToCustomer
        )
      } catch (error: any) {
        error.description = CheckoutErrorType.PAYMENT_METHOD_ERROR
        this.checkoutError = CheckoutErrorType.PAYMENT_METHOD_ERROR
        this.isOrderPending = false
        throw error
      }

      // Add CS fields
      try {
        payload = _addCsFieldsToPayload(payload, this)
      } catch (error: any) {
        error.description = CheckoutErrorType.FALLBACK_CREATE_ORDER_ERROR
        this.checkoutError = CheckoutErrorType.FALLBACK_CREATE_ORDER_ERROR
        console.error('Error preparing order cs payload', error)
        this.isOrderPending = false
        throw error
      }

      // Step 4: Execute strategy's modifyPayload hook
      if (strategy.modifyPayload) {
        payload = strategy.modifyPayload(payload)
      }

      // Step 5: Perform 3DS check
      try {
        payload = await this._performThreeDSecureCheck(
          payload,
          cartStore,
          isGuest
        )
      } catch (error: any) {
        error.description = CheckoutErrorType.PAYMENT_METHOD_ERROR
        this.checkoutError = CheckoutErrorType.PAYMENT_METHOD_ERROR
        console.error('3DS error:', error)
        this.isOrderPending = false

        // Stale nonce (e.g. cached token expired): clear so user is redirected to re-enter card
        if (error?.code === 'THREEDS_LOOKUP_VALIDATION_ERROR') {
          clearBraintreeClient()
          if (isGuest) {
            useGuestCheckoutStore().selectedPaymentMethodData = null
          } else {
            const regularStore = useRegularCheckoutStore()
            regularStore.selectedPaymentMethodData = null
            regularStore.selectedPaymentMethodId = null
          }
        }

        throw error
      }

      // Step 6: Handle customer data (age/phone)
      // For express orders: add directly to payload
      // For authenticated non-express orders: update customer profile via API
      if (useExpressOrder) {
        const ageData = strategy.getAgeData()
        const phone = strategy.getPhoneNumber()

        if (ageData) {
          const { year, month, day } = ageData
          payload.dob = `${year}-${month}-${day}`
        }

        if (phone) {
          payload.phone = phone
        }
      } else if (!isGuest) {
        await _updateCustomerData({
          dob: strategy.getAgeData(),
          phone: strategy.getPhoneNumber()
        })
      }

      // Step 7: Place order using appropriate API endpoint
      try {
        const data = useExpressOrder
          ? await ApiController.placeExpressOrder(toRaw(payload))
          : await ApiController.placeOrder(toRaw(payload))

        if (useExpressOrder) {
          pushCustomerDataToElbwalker(data)
        }

        // Persist email to sessionStorage for use on success page
        if (email) {
          sessionStorage.setItem(SESSION_EMAIL_KEY, email)
        }

        // Step 8: Cleanup (only after successful order placement)
        // Clear age data and phone number from strategy store
        strategy.clearAgeAndPhoneData()
        this.afterPurchase(strategy)
        return data
      } catch (error: any) {
        this.isOrderPending = false
        this._handleOrderError(error, strategy)
        throw error
      }
    },

    /**
     * Performs 3DS secure check on payment method
     */
    async _performThreeDSecureCheck(
      payload: any,
      cartStore: ReturnType<typeof useCartStore>,
      isGuestCheckout: boolean
    ): Promise<any> {
      const paymentStore = usePaymentStore()
      const nonce = await paymentStore.performThreeDSecureCheck({
        shippingAddressId: payload.shipping_address_id,
        shippingAddressData: payload.shipping_address,
        billingAddressId: payload.billing_address_id,
        billingAddressData: payload.billing_address,
        paymentMethodId: payload.payment_method_id,
        paymentMethodData: payload.payment_method,
        email: payload.email,
        amount: toRaw(cartStore.instance.grandTotal),
        isGuestCheckout
      })

      if (nonce) {
        payload.braintree_nonce = nonce
      }

      // Clean up payment_method: remove sensitive/internal fields
      payload.payment_method = paymentStore.cleanPaymentMethodForApi(
        payload.payment_method as Record<string, any> | undefined
      )

      return payload
    },

    /**
     * Handles order errors by mapping causes to error types and blacklisting payment methods
     */
    _handleOrderError(error: any, strategy: CheckoutStrategy): void {
      const paymentIdentifier = strategy.getPaymentMethodIdentifier()

      // Set payment identifier on error for blacklisting callback
      if (paymentIdentifier) {
        if (typeof paymentIdentifier === 'number') {
          error.paymentMethodId = paymentIdentifier
        } else {
          error.paymentMethodCode = paymentIdentifier
        }
      }

      // Check for payment errors in description (OPC specific)
      const isPaymentErrorInDescription =
        error.description &&
        typeof error.description === 'string' &&
        error.description.toLowerCase().includes('payment')

      if (isPaymentErrorInDescription) {
        error.status = 400 // Treat as payment error
      }

      // Preserve original API description before handleCheckoutError overwrites it
      const originalDescription = error.description

      const errorType = handleCheckoutError(
        error,
        strategy.getUseSameBillingAddress(),
        paymentIdentifier
          ? (idOrCode) => strategy.blacklistPaymentMethod(idOrCode)
          : undefined,
        () => strategy.clearPaymentMethod()
      )

      // Store detailed address error for field-level display
      if (
        errorType === CheckoutErrorType.SHIPPING_ADDRESS_ERROR ||
        errorType === CheckoutErrorType.BILLING_ADDRESS_ERROR
      ) {
        this.checkoutAddressError = {
          description: originalDescription,
          field: error.field,
          fields: error.fields,
          address_type: error.address_type
        }
      } else {
        this.checkoutAddressError = null
      }

      this.checkoutError = errorType
    },

    afterPurchase(strategy: CheckoutStrategy) {
      // Let the strategy handle its own cleanup
      strategy.afterPurchaseCleanup(this)

      // Clear cached Braintree client so next checkout gets a fresh token
      clearBraintreeClient()

      // Common cleanup for all checkout types
      const cartStore = useCartStore()
      const customerStore = useCustomerStore()
      cartStore.reset()
      cartStore.removeCouponCode()
      cartStore.removeMilesFromCart()
      customerStore.fetchProfile(true)
    },
    abortCheckout() {
      this.checkoutError = null
      this.checkoutAddressError = null
      this.clearData()
    },
    clearData() {
      this.storeDataToCustomer = true
      this.isOrderPending = false
      this.checkoutError = null
      this.checkoutAddressError = null
      this.csScheduledShipmentDate = null
      clearBraintreeClient()
    }
  }
})

const _updateCustomerData = async ({
  dob,
  phone
}: {
  dob?: any
  phone?: string | null
}): Promise<void> => {
  const payload: Record<string, any> = {}

  if (dob && typeof dob === 'object') {
    const { year, month, day } = dob
    if (year && month && day) {
      payload.dob = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    }
  } else if (dob && typeof dob === 'string') {
    payload.dob = dob
  }

  if (phone) {
    payload.phone = phone
  }

  const customerStore = useCustomerStore()
  await customerStore.updateCustomerData(payload)
}

const _addCsFieldsToPayload = (payload: any, store: any) => {
  const csApiFields: Record<string, string> = {
    csReasonForOrder: 'cs_reason_for_order',
    csCreateNoCredits: 'cs_create_no_credits',
    csCreateNoConfirmationMail: 'cs_create_no_confirmation_mail',
    csCreateNoShippingMail: 'cs_create_no_shipping_mail',
    csCreateNoInvoiceMail: 'cs_create_no_invoice_mail',
    csComment: 'cs_comment',
    csScheduledShipmentDate: 'cs_scheduled_shipment_date'
  }

  for (const [storeKey, payloadKey] of Object.entries(csApiFields)) {
    if (store[storeKey]) {
      payload[payloadKey] = store[storeKey]
    }
  }

  return payload
}

/**
 * Pushes customer data to elbwalker for tracking purposes.
 * This method is public to allow for better testability.
 * @param data - Order data containing customer information
 */
export const pushCustomerDataToElbwalker = (data: any): void => {
  if (!data) return

  const elbData: any = {
    customerId: data.customer_id ?? null,
    completedOrders: data.completed_orders ?? null,
    lastOrderDaysAgo: data.last_order_days_ago ?? null
  }
  elb(WALKER_COMMANDS.run, { user: elbData })
}
